import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Box,
  Skeleton,
  TablePagination,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  IconButton,
} from "@mui/material";
import { Check, Clear, Close as CloseIcon } from "@mui/icons-material";

// --- Table Styles ---
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  maxWidth: "140px",
  overflow: "hidden",
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.light,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
  "& svg": {
    position: "relative",
    top: "5px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme, isIssue }) => ({
  backgroundColor: isIssue ? "#ffebee" : "inherit",
  cursor: "pointer",
  
  "&:nth-of-type(odd)": {
    backgroundColor: isIssue ? "#ffebee" : theme.palette.action.hover,
  },

  "&:hover": {
    backgroundColor: isIssue ? "#ffcdd2" : theme.palette.action.selected,
  },
  
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

// --- Dialog Styles (Matching Reference) ---
const StyledHeaderTitle = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: "#fff",
  padding: "10px 15px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: "flex",
  marginBottom: theme.spacing(1),
  alignItems: "flex-start",
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  minWidth: "180px",
  color: theme.palette.text.secondary,
  fontSize: "0.9rem",
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: "0.9rem",
  wordBreak: "break-word",
}));

const IssueBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffebee",
  border: "1px solid #ef9a9a",
  borderRadius: "4px",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  color: "#c62828",
}));

const TableComponent3PL = ({ 
  columns, 
  rows, 
  loading, 
  page, 
  rowsPerPage, 
  total, 
  from, 
  to,
  handleChangePage,
  handleChangeRowsPerPage 
}) => {
  // State for Dialog
  console.log("Columns:", columns);
  console.log("Rows:", rows);
  const [open, setOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const handleClickOpen = (row) => {
    setSelectedOrder(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedOrder(null);
  };

  const renderCellValue = (row, colId) => {
    if (colId === "shipComplete") {
      return row.shipCompleteDate ? <Check color="primary" /> : <Clear color="error" />;
    }
    return row[colId] || "-";
  };

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 780 }}>
          <Table stickyHeader aria-label="sticky table" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    style={{ minWidth: 150 }}
                  >
                    {column.name}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Box p={3}>
                      <Skeleton height={60} />
                      <Skeleton height={60} />
                      <Skeleton height={60} />
                      <Skeleton height={60} />
                      <Skeleton height={60} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows.map((row) => (
                  <StyledTableRow
                    key={row.netsuiteOrderNumber}
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    isIssue={row.isThreePLIssueOrder === 1}
                    onClick={() => handleClickOpen(row)}
                  >
                    {columns.map((column) => (
                      <StyledTableCell key={column.id}>
                        {renderCellValue(row, column.id)}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Typography variant="body1">No Orders Found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[20, 50, 70, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={() => `${from}-${to} of ${total}`}
        />
      </Paper>

      {/* --- Details Dialog (Matching Reference Header) --- */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {/* Custom Header matching your reference */}
        <StyledHeaderTitle id="alert-dialog-title">
          <div>
            Order Details {"(" +
              (selectedOrder?.poNumber || "-") +
              "/" +
              (selectedOrder?.netsuiteOrderNumber || "-") +
              ")"}
          </div>
          <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </StyledHeaderTitle>
        
        <DialogContent sx={{ padding: "0" }}>
          {selectedOrder && (
            <Box p={3}>
              {/* Highlighted Issue Message */}
              {selectedOrder.isThreePLIssueOrder === 1 && (
                <IssueBox>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                    <Clear fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                    Order Issue Detected
                  </Typography>
                  <Typography variant="body2">
                    {selectedOrder.threePLIssueMessage || "No message provided."}
                  </Typography>
                </IssueBox>
              )}

              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <DetailRow>
                    <DetailLabel>PO Number:</DetailLabel>
                    <DetailValue>{selectedOrder.poNumber || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>NetSuite Order #:</DetailLabel>
                    <DetailValue>{selectedOrder.netsuiteOrderNumber || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Customer Name:</DetailLabel>
                    <DetailValue>{selectedOrder.customerName || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Assigned Branch:</DetailLabel>
                    <DetailValue>{selectedOrder.assignBranch || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Order Created:</DetailLabel>
                    <DetailValue>{selectedOrder.orderCreated || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Ready Date:</DetailLabel>
                    <DetailValue>{selectedOrder.readyDate || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Allocate Date:</DetailLabel>
                    <DetailValue>{selectedOrder.allocateDate || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Picking Date:</DetailLabel>
                    <DetailValue>{selectedOrder.pickingDate || "-"}</DetailValue>
                  </DetailRow>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <DetailRow>
                    <DetailLabel>Despatch Generated:</DetailLabel>
                    <DetailValue>{selectedOrder.despatchGeneratedDate || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Closed Date:</DetailLabel>
                    <DetailValue>{selectedOrder.closedDate || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Manifest Call Date:</DetailLabel>
                    <DetailValue>{selectedOrder.manifestCallDate || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Cust Must Ship By:</DetailLabel>
                    <DetailValue>{selectedOrder.custMustShipByDate || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Order Status:</DetailLabel>
                    <DetailValue sx={{ fontWeight: "bold" }}>{selectedOrder.orderStatus || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Ship Complete:</DetailLabel>
                    <DetailValue>
                      {selectedOrder.shipCompleteDate ? (
                        <span><Check fontSize="small" color="success" style={{ verticalAlign: "middle" }} /> {selectedOrder.shipCompleteDate}</span>
                      ) : (
                        <Clear fontSize="small" color="error" style={{ verticalAlign: "middle" }} />
                      )}
                    </DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Is 3PL Issue Order:</DetailLabel>
                    <DetailValue>
                      {selectedOrder.isThreePLIssueOrder === 1 ? (
                        <span style={{ color: "red", fontWeight: "bold" }}>Yes</span>
                      ) : (
                        "No"
                      )}
                    </DetailValue>
                  </DetailRow>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TableComponent3PL;